/**
 * Binary stroke encoding/decoding for browser-side ArrayBuffer transport.
 *
 * Record layout (20 bytes per stroke) — matches server-side strokes.js:
 *   Offset  Size  Field
 *   0       4     sid        (uint32 LE)
 *   4       2     x  * 10   (uint16 LE)
 *   6       2     y  * 10   (uint16 LE)
 *   8       2     x2 * 10   (uint16 LE)
 *   10      2     y2 * 10   (uint16 LE)
 *   12      1     size       (uint8)
 *   13      1     flags      type(2 bits) | blender(3 bits) | reserved(3 bits)
 *   14      1     R          (uint8)
 *   15      1     G          (uint8)
 *   16      1     B          (uint8)
 *   17      1     A          (uint8)
 *   18      2     padding
 */

export const RECORD_SIZE = 20;

// ── Color parsing ───────────────────────────────────────────────────────────

const COLOR_RE = /rgba?\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*(?:,\s*(\d+(?:\.\d+)?))?\s*\)/;

export function parseColor(colorStr) {
    if (!colorStr) return { r: 0, g: 0, b: 0, a: 255 };
    if (colorStr.startsWith('#')) {
        const hex = colorStr.slice(1);
        if (hex.length === 3) {
            return {
                r: parseInt(hex[0] + hex[0], 16),
                g: parseInt(hex[1] + hex[1], 16),
                b: parseInt(hex[2] + hex[2], 16),
                a: 255,
            };
        }
        return {
            r: parseInt(hex.slice(0, 2), 16) || 0,
            g: parseInt(hex.slice(2, 4), 16) || 0,
            b: parseInt(hex.slice(4, 6), 16) || 0,
            a: hex.length >= 8 ? parseInt(hex.slice(6, 8), 16) : 255,
        };
    }
    const m = COLOR_RE.exec(colorStr);
    if (!m) return { r: 0, g: 0, b: 0, a: 255 };
    const alpha = m[4] !== undefined ? parseFloat(m[4]) : 1;
    return {
        r: Math.round(parseFloat(m[1])),
        g: Math.round(parseFloat(m[2])),
        b: Math.round(parseFloat(m[3])),
        a: alpha <= 1 ? Math.round(alpha * 255) : Math.round(alpha),
    };
}

export function colorToString(r, g, b, a) {
    if (a === 255) return `rgb(${r},${g},${b})`;
    return `rgba(${r},${g},${b},${(a / 255).toFixed(3)})`;
}

// ── Coordinate helpers ──────────────────────────────────────────────────────

function clampCoord(val) {
    return Math.max(0, Math.min(65535, Math.round((val || 0) * 10)));
}

// ── Encode / Decode ─────────────────────────────────────────────────────────

/**
 * Encode an array of stroke objects into an ArrayBuffer.
 * Each stroke: { sid?, x, y, x2, y2, s, t, c, b }
 * sid is optional on the client side (server assigns them).
 * Returns an ArrayBuffer.
 */
export function encodeStrokes(strokes) {
    const ab = new ArrayBuffer(strokes.length * RECORD_SIZE);
    const dv = new DataView(ab);
    for (let i = 0; i < strokes.length; i++) {
        const s = strokes[i];
        const off = i * RECORD_SIZE;
        const color = parseColor(s.c);

        dv.setUint32(off, (s.sid || 0) >>> 0, true);
        dv.setUint16(off + 4, clampCoord(s.x), true);
        dv.setUint16(off + 6, clampCoord(s.y), true);
        dv.setUint16(off + 8, clampCoord(s.x2), true);
        dv.setUint16(off + 10, clampCoord(s.y2), true);
        dv.setUint8(off + 12, Math.max(0, Math.min(255, Math.round(s.s || 1))));
        const flags = ((s.t & 0x3) << 6) | (((s.b || 0) & 0x7) << 3);
        dv.setUint8(off + 13, flags);
        dv.setUint8(off + 14, color.r);
        dv.setUint8(off + 15, color.g);
        dv.setUint8(off + 16, color.b);
        dv.setUint8(off + 17, color.a);
        // bytes 18-19 are padding (already 0)
    }
    return ab;
}

/**
 * Decode an ArrayBuffer (or Buffer/Uint8Array) into an array of stroke objects.
 * Returns array of { sid, x, y, x2, y2, s, t, c, b }.
 */
export function decodeStrokes(data) {
    // Normalize to ArrayBuffer — msgpack may deliver a Buffer, Uint8Array, or ArrayBuffer.
    let ab;
    if (data instanceof ArrayBuffer) {
        ab = data;
    } else if (data && data.buffer instanceof ArrayBuffer) {
        // Uint8Array or Node Buffer view — use a byte-aligned copy to avoid
        // DataView alignment issues when the view has a non-zero byteOffset.
        ab = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength);
    } else {
        return [];
    }

    const dv = new DataView(ab);
    const count = Math.floor(ab.byteLength / RECORD_SIZE);
    const result = new Array(count);
    for (let i = 0; i < count; i++) {
        const off = i * RECORD_SIZE;
        const flags = dv.getUint8(off + 13);
        const r = dv.getUint8(off + 14);
        const g = dv.getUint8(off + 15);
        const b = dv.getUint8(off + 16);
        const a = dv.getUint8(off + 17);
        result[i] = {
            sid: dv.getUint32(off, true),
            x: dv.getUint16(off + 4, true) / 10,
            y: dv.getUint16(off + 6, true) / 10,
            x2: dv.getUint16(off + 8, true) / 10,
            y2: dv.getUint16(off + 10, true) / 10,
            s: dv.getUint8(off + 12),
            t: (flags >> 6) & 0x3,
            b: (flags >> 3) & 0x7,
            c: colorToString(r, g, b, a),
        };
    }
    return result;
}
